/**
 * Organizations list hook using React Relay.
 *
 * Server-side search (name ilike) and cursor pagination over listOrganizations.
 * The query is token-scoped: the API only returns organizations the current
 * admin can access, so no explicit access filter is needed.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useOrganizationsRelayDirectQuery as UseOrganizationsRelayDirectQuery,
  useOrganizationsRelayDirectQuery$variables,
  OrganizationFilterInput,
  OrganizationSortInput,
} from './__generated__/useOrganizationsRelayDirectQuery.graphql';

const OrganizationsListDirectQuery = graphql`
  query useOrganizationsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: OrganizationFilterInput
    $sort: [OrganizationSortInput!]
  ) {
    listOrganizations(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
        }
      }
    }
  }
`;

export type { OrganizationFilterInput, OrganizationSortInput };

export interface OrganizationGraphQL {
  id: string;
  name: string;
}

/**
 * Simplified filter options for common use cases
 */
export interface OrganizationQueryFilter {
  search?: string;
}

function buildRelayFilter(
  filter?: OrganizationQueryFilter
): OrganizationFilterInput | undefined {
  if (!filter?.search) return undefined;
  return { name: { ilike: `%${filter.search}%` } };
}

/**
 * Hook that provides React Query-like interface for the organizations list.
 *
 * Does NOT use Suspense - provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, count, hasMore, endCursor, isLoading } = useOrganizationsRelayQuery(
 *   { search: 'acme' },
 *   { limit: 25, after: afterCursor }
 * );
 */
export function useOrganizationsRelayQuery(
  filter?: OrganizationQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: OrganizationSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<OrganizationGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useOrganizationsRelayDirectQuery$variables = {
          first: limit,
          after,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseOrganizationsRelayDirectQuery>(
          relayEnvironment,
          OrganizationsListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listOrganizations?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData(nodes as OrganizationGraphQL[]);
          setCount(result.listOrganizations?.count ?? undefined);
          setHasMore(result.listOrganizations?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listOrganizations?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch organizations'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}
